/**
 * Utility pre Tabulator progressive loading s remote filterom.
 * - Deduplikácia requestov (žiadne dvojité volanie pre ten istý podpis)
 * - Bezpečná serializácia (žiadne [object Object] z Tabulator filter objektu)
 * - Čisté sorters [{field, dir}]
 */

import {
  normalizeIdColumnInLayoutNode,
  TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from './tabulatorIdColumn';
import { WORKFLOW_NEXT_STEP_PHASES } from './workflowNextStepLabel';
import {
  applySupportTicketListScopeStatusParam,
  isSupportTicketScopeValue,
  parseSupportTicketStatusHeaderValues,
} from './supportTicketStatus';
import { uiT } from './uiTranslate';

export { TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS };

/** Deterministic query string – rovnaké params => rovnaký string */
export function stableStringify(params) {
  if (!params || typeof params !== "object") return "";
  const sorted = Object.keys(params)
    .filter(k => params[k] !== undefined && params[k] !== null)
    .sort();
  return sorted.map(k => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`).join("&");
}

/** Normalizácia sorters na [{field, dir}] – nikdy Tabulator column objekty */
export function cleanSorters(sorters) {
  if (!Array.isArray(sorters) || sorters.length === 0) return [];
  return sorters
    .map(s => {
      const field = typeof s?.field === "string" ? s.field : (s?.column?.getField?.() ?? s?.column);
      const dir = s?.dir === "asc" || s?.dir === "desc" ? s.dir : "asc";
      return typeof field === "string" && field ? { field, dir } : null;
    })
    .filter(Boolean);
}

/** Parsovanie uložených sorters z DB – validácia formátu */
export function parseSorters(val) {
  if (!val) return [];

  try {
    const arr =
      typeof val === "string"
        ? JSON.parse(val)
        : Array.isArray(val)
        ? val
        : [];

    if (!Array.isArray(arr)) return [];

    return arr
      .map((s) => {
        const rawField = s?.column ?? s?.field ?? null;
        if (typeof rawField !== "string" || !rawField.trim()) return null;

        const dir = s?.dir === "desc" ? "desc" : "asc";

        return {
          column: rawField,  // pre UI setSort
          field: rawField,   // pre API
          dir,
        };
      })
      .filter(Boolean);
  } catch (e) {
    console.warn("parseSorters failed:", e);
    return [];
  }
}


/** Bezpečné parsovanie JSON (pre attributes, columns, atď.) */
export function safeParse(val, fallback) {
  if (val === null || val === undefined) return fallback;
  if (typeof val === "string") { try { return JSON.parse(val); } catch { return fallback; } }
  return val;
}

/**
 * Odstráni z uloženého Tabulator layoutu lokalizačne závislé texty, aby ich
 * po restore neprepísali staré `title` hodnoty z DB.
 */
function sanitizeTabulatorLayoutColumnNode(node, options = {}) {
  if (!node || typeof node !== "object" || Array.isArray(node)) return null;
  let cloned = normalizeIdColumnInLayoutNode({ ...node }, options);
  delete cloned.title;
  delete cloned.headerTooltip;
  if (Array.isArray(cloned.columns)) {
    cloned.columns = cloned.columns
      .map((child) => sanitizeTabulatorLayoutColumnNode(child, options))
      .filter(Boolean);
  }
  return cloned;
}

/**
 * @param {unknown} attributes
 * @param {{ forceFrozenId?: boolean }} [options] — `forceFrozenId: true` len pre flat remote Skupinu A (V9d-1)
 */
export function sanitizeTabulatorLayoutAttributes(attributes, options = {}) {
  if (!Array.isArray(attributes)) return [];
  return attributes
    .map((node) => sanitizeTabulatorLayoutColumnNode(node, options))
    .filter(Boolean);
}

/** Parsovanie mask filters z DB */
export function parseMaskFilters(val) {
  if (val == null) return [];
  try {
    return typeof val === "string" ? JSON.parse(val) : Array.isArray(val) ? val : [];
  } catch {
    return [];
  }
}

/** Prvý riadok user_mask_filters pre danú stránku (`page` v DB). */
export function findUserMaskRow(userMaskFilters, maskPage) {
  if (!maskPage || !Array.isArray(userMaskFilters)) return null;
  return userMaskFilters.find((m) => m.page === maskPage) ?? null;
}

/**
 * Tabulator `initialSort` zladené s maskou / fallbackom (zhoda s prvým `mergePagedSortersIntoExtra` + `buildRequestParams`).
 *
 * **Nepoužívať prázdne `initialSort` pri maske** na tabuľkách s `dataTree` + `dataTreeElementColumn`:
 * Tabulator po načítaní dát doplní sort na stromový stĺpec → `sortChanged` → druhý `paged` s iným `sorters`.
 * Preto pri uloženom triedení v maske vrátime **rovnaké** sorters ako `initialSort` (duplicitné `setSort` v
 * `applyUserMaskRowToTabulator` je v poriadku).
 *
 * @param {object} opts
 * @param {Array|null|undefined} opts.userMaskFilters
 * @param {string} opts.maskPage
 * @param {boolean} [opts.skipMask=false] – napr. preview režim, kde sa maska do requestu neberie
 * @param {Array<{ column?: string, field?: string, dir?: string }>} [opts.defaultSort] – Tabulator tvar (`column` alebo `field`)
 */
export function getTabulatorInitialSortUnlessMaskHasSorters({
  userMaskFilters,
  maskPage,
  skipMask = false,
  defaultSort = [{ column: "machine.name", dir: "asc" }],
}) {
  const normalize = (arr) =>
    arr.map((s) => ({
      column: s.column ?? s.field,
      dir: s.dir === "desc" ? "desc" : "asc",
    }));

  if (skipMask) {
    return normalize(defaultSort);
  }
  const maskRow = findUserMaskRow(userMaskFilters, maskPage);
  if (maskRow?.sorters) {
    const parsed = cleanSorters(parseSorters(maskRow.sorters));
    if (parsed.length > 0) {
      return normalize(parsed);
    }
  }
  return normalize(defaultSort);
}

/**
 * Tabulator `initialHeaderFilter` from Redux mask.
 *
 * Applied in Filter.tableBuilt while `tableInitialized === false`, so
 * setHeaderFilterValue → Filter.success does **not** call refreshFilter/reloadData.
 * Prefer this over post-built setHeaderFilterValue (third arg is not silent in 5.5.2).
 *
 * @param {object} opts
 * @param {Array|null|undefined} opts.userMaskFilters
 * @param {string} opts.maskPage
 * @param {boolean} [opts.skipMask=false]
 * @param {(field: string, value: *) => *|null|undefined} [opts.normalizeValue]
 *        Return null/undefined to skip the filter entry.
 * @returns {Array<{ field: string, value: * }>}
 */
export function buildTabulatorInitialHeaderFilterFromMask({
  userMaskFilters,
  maskPage,
  skipMask = false,
  normalizeValue,
}) {
  if (skipMask) return [];
  const maskRow = findUserMaskRow(userMaskFilters, maskPage);
  if (!maskRow) return [];
  const filters = parseMaskFilters(maskRow.filters);
  const out = [];
  for (const f of filters) {
    if (!f?.field) continue;
    let v = f.value;
    if (typeof normalizeValue === "function") {
      v = normalizeValue(f.field, v);
      if (v === null || v === undefined) continue;
    } else if (v === undefined || v === null || String(v).trim() === "") {
      continue;
    }
    out.push({ field: f.field, value: v });
  }
  return out;
}

/** `getHeaderFilters()` niekedy vráti objekt – zjednotenie na pole `{ field, value }`. */
export function coerceHeaderFilterList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return Object.entries(raw).map(([field, value]) => ({ field, value }));
  return [];
}

/** Bodky v poli → podčiarkovníky pre `filter_*` (remote paged API). */
export function filterFieldDotsToUnderscore(field) {
  return field && typeof field === "string" && field.includes(".") ? field.replace(/\./g, "_") : field;
}

export const MASK_SORTERS_WHEN = Object.freeze({
  /** Masku použiť len kým `tableBuiltRef` ešte nie je true. */
  TABLE_NOT_BUILT: "table_not_built",
  /** Masku použiť, keď Tabulator nemá žiadne triedenie (aj po zostavení tabuľky). */
  NO_TABLE_SORTERS: "no_table_sorters",
});

/**
 * Nastaví `extra.sorters` z `getSorters()` alebo z uloženej masky (`cleanSorters` + `parseSorters`).
 */
export function mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams, maskRow, when = MASK_SORTERS_WHEN.TABLE_NOT_BUILT }) {
  // Guard: Tabulator warns "Table Not Initialized" if getSorters runs before tableBuilt.
  const tableReadyForSorters = !!tableBuiltForParams && !!table && table.initialized !== false;
  const sortersFromTable = tableReadyForSorters ? (table?.getSorters?.() ?? []) : [];
  const hasTableSorters = Array.isArray(sortersFromTable) && sortersFromTable.length > 0;
  if (hasTableSorters) {
    const clean = cleanSorters(sortersFromTable);
    if (clean.length > 0) {
      extra.sorters = JSON.stringify(clean);
      return;
    }
  }
  if (!maskRow?.sorters) return;
  const cleanMask = cleanSorters(parseSorters(maskRow.sorters));
  if (cleanMask.length === 0) return;
  if (when === MASK_SORTERS_WHEN.TABLE_NOT_BUILT) {
    if (!tableBuiltForParams) extra.sorters = JSON.stringify(cleanMask);
  } else if (when === MASK_SORTERS_WHEN.NO_TABLE_SORTERS) {
    extra.sorters = JSON.stringify(cleanMask);
  }
}

function appendLiteralFieldPagedHeaderFilter(f, extra) {
  if (f?.field == null) return;
  const v = f.value;
  if (v === undefined || v === null || typeof v === "object") return;
  const str = String(v).trim();
  if (str === "") return;
  extra[`filter_${f.field}`] = str;
}

function appendDotUnderscorePagedFilter(f, extra) {
  if (f?.field == null) return;
  const v = f.value;
  if (v === undefined || v === null || String(v).trim() === "") return;
  extra[`filter_${filterFieldDotsToUnderscore(f.field)}`] = v;
}

/**
 * Remote paged zoznam: pred `tableBuilt` filtre a triedenie z Redux masky, potom z Tabulatora.
 * `filterKeyStyle: 'literal_field'` = `filter_${field}` bez náhrady bodiek (Summary email); inak bodky → `_`.
 */
export function mergePagedStandardListFiltersAndSorters(extra, {
  getTable,
  tableBuiltForParams,
  userMaskFilters,
  maskPage,
  useMaskFromRedux = true,
  filterKeyStyle = "dot_underscore",
  appendFilter,
  sortersWhen = MASK_SORTERS_WHEN.TABLE_NOT_BUILT,
}) {
  const table = typeof getTable === "function" ? getTable() : getTable;
  const maskRow = useMaskFromRedux && userMaskFilters && maskPage ? findUserMaskRow(userMaskFilters, maskPage) : null;

  let hf = [];
  if (tableBuiltForParams && table) hf = coerceHeaderFilterList(table.getHeaderFilters?.());
  if (tableBuiltForParams && hf.length > 0) {
    hf.forEach((f) => {
      if (appendFilter) appendFilter(f, extra);
      else if (filterKeyStyle === "literal_field") appendLiteralFieldPagedHeaderFilter(f, extra);
      else appendDotUnderscorePagedFilter(f, extra);
    });
  } else if (!tableBuiltForParams && maskRow) {
    parseMaskFilters(maskRow.filters).forEach((f) => {
      if (appendFilter) appendFilter(f, extra);
      else if (filterKeyStyle === "literal_field") {
        if (!f?.field) return;
        const v = f.value;
        if (v === undefined || v === null || String(v).trim() === "") return;
        extra[`filter_${f.field}`] = v;
      } else appendDotUnderscorePagedFilter(f, extra);
    });
  }

  mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams, maskRow, when: sortersWhen });
}

/**
 * Checklist state list: ak je aspoň jeden neprázdny header filter, pošlú sa všetky stĺpce (vrátane prázdnych);
 * inak sa použijú filtre z masky. Triedenie: tabuľka alebo maska (`NO_TABLE_SORTERS`).
 */
export function mergePagedChecklistStateListFiltersAndSorters(extra, {
  table,
  tableBuiltForParams,
  userMaskFilters,
  maskPage,
  preview,
}) {
  const maskRow = !preview && userMaskFilters && maskPage ? findUserMaskRow(userMaskFilters, maskPage) : null;

  const applyMaskFilters = () => {
    if (!maskRow) return;
    parseMaskFilters(maskRow.filters).forEach((f) => {
      if (!f?.field || f.value == null || String(f.value).trim() === "") return;
      extra[`filter_${f.field}`] = f.field === "is_unread" ? String(f.value) : f.value;
    });
  };

  if (tableBuiltForParams && table?.getHeaderFilters) {
    const hfa = table.getHeaderFilters() || [];
    const hasAnyHeader = hfa.some((f) => {
      const v = f?.value;
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
    if (hasAnyHeader) {
      hfa.forEach((f) => {
        if (f?.field == null) return;
        const v = f.value;
        const key = `filter_${f.field}`;
        const isEmpty = v === undefined || v === null || String(v).trim() === "";
        extra[key] = isEmpty ? "" : f.field === "is_unread" ? String(v) : v;
      });
    } else {
      applyMaskFilters();
    }
    mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams: true, maskRow, when: MASK_SORTERS_WHEN.NO_TABLE_SORTERS });
  } else if (maskRow) {
    applyMaskFilters();
    mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams, maskRow, when: MASK_SORTERS_WHEN.NO_TABLE_SORTERS });
  }
}

/**
 * mntn028: checklist detail — vždy machines_only + filter_template_machine_id, potom checklist merge filtrov/sorterov.
 */
export function mergePagedChecklistMachineDetailFiltersAndSorters(extra, {
  getTable,
  tableBuiltForParams,
  userMaskFilters,
  maskPage,
  machineId,
}) {
  extra.machines_only = 1;
  if (machineId != null) extra.filter_template_machine_id = machineId;
  const table = typeof getTable === 'function' ? getTable() : getTable;
  mergePagedChecklistStateListFiltersAndSorters(extra, {
    table,
    tableBuiltForParams,
    userMaskFilters,
    maskPage,
  });
}

function collectChecklistLayoutFields(attributes) {
  const fields = [];
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.field) fields.push(String(node.field));
      if (Array.isArray(node.columns)) walk(node.columns);
    }
  };
  walk(attributes);
  return fields;
}

function flattenChecklistLayoutNodesByField(attributes) {
  const map = new Map();
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.field) map.set(String(node.field), node);
      if (Array.isArray(node.columns)) walk(node.columns);
    }
  };
  walk(attributes);
  return map;
}

/**
 * Zlúči uložený Tabulator column layout pre checklist detail (mntn028).
 * Layout bez poľa `id` → null (staré masky pred ID).
 */
export function mergeChecklistDetailColumnLayoutAttributes(savedAttributes, canonicalFieldOrder, layoutSanitizeOptions) {
  const sanitized = sanitizeTabulatorLayoutAttributes(savedAttributes, layoutSanitizeOptions);
  if (!Array.isArray(sanitized) || sanitized.length <= 1) return null;

  const savedFields = collectChecklistLayoutFields(sanitized);
  if (!savedFields.includes('id')) return null;

  const canonicalSet = new Set(canonicalFieldOrder);
  const byField = flattenChecklistLayoutNodesByField(sanitized);
  const orderedNodes = [];
  const seen = new Set();

  const walkPreserveOrder = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue;
      if (node.field) {
        const field = String(node.field);
        if (canonicalSet.has(field) && !seen.has(field)) {
          seen.add(field);
          orderedNodes.push(byField.get(field) || node);
        }
      }
      if (Array.isArray(node.columns)) walkPreserveOrder(node.columns);
    }
  };
  walkPreserveOrder(sanitized);

  for (const field of canonicalFieldOrder) {
    if (seen.has(field)) continue;
    if (byField.has(field)) {
      orderedNodes.push(byField.get(field));
      seen.add(field);
      continue;
    }
    orderedNodes.push({ field });
    seen.add(field);
  }

  return orderedNodes.length > 1 ? orderedNodes : null;
}

export function applyChecklistDetailColumnLayout(table, savedAttributes, canonicalFieldOrder, layoutSanitizeOptions) {
  if (!table) return;
  const layout = mergeChecklistDetailColumnLayoutAttributes(savedAttributes, canonicalFieldOrder, layoutSanitizeOptions);
  if (!layout) return;
  try {
    table.setColumnLayout?.(layout);
  } catch (_) {}
}

/**
 * Obnoví user masku na checklist detail tabuľke (layout → filtre/sort/viditeľnosť).
 */
export function restoreChecklistDetailUserMaskToTable(table, maskRow, options = {}) {
  if (!table || !maskRow) return;
  const {
    canonicalFieldOrder,
    setColumnsVisible,
    applyColumnVisibilityFn,
    clearHeaderFiltersWhenEmpty = true,
    forceFrozenId = false,
  } = options;
  if (!canonicalFieldOrder) return;

  const layoutSanitizeOptions = forceFrozenId ? { forceFrozenId: true } : undefined;
  applyChecklistDetailColumnLayout(table, safeParse(maskRow.attributes, []), canonicalFieldOrder, layoutSanitizeOptions);
  applyUserMaskRowToTabulator(table, maskRow, {
    applyLayout: false,
    setColumnsVisible,
    clearHeaderFiltersWhenEmpty,
    applyColumnVisibilityFn,
    applyFilters: true,
    applySort: true,
    forceFrozenId,
  });
}

/** V6a company listy (mntn036/003/005/007) — rovnaká logika layout merge ako checklist detail. */
export const mergeCompanyListColumnLayoutAttributes = mergeChecklistDetailColumnLayoutAttributes;
export const applyCompanyListColumnLayout = applyChecklistDetailColumnLayout;

export function restoreCompanyListUserMaskToTable(table, maskRow, options = {}) {
  if (!table || !maskRow) return;
  const {
    canonicalFieldOrder,
    setColumnsVisible,
    applyColumnVisibilityFn,
    clearHeaderFiltersWhenEmpty = true,
    forceFrozenId = false,
  } = options;
  if (!canonicalFieldOrder) return;

  const layoutSanitizeOptions = forceFrozenId ? { forceFrozenId: true } : undefined;
  applyCompanyListColumnLayout(table, safeParse(maskRow.attributes, []), canonicalFieldOrder, layoutSanitizeOptions);
  applyUserMaskRowToTabulator(table, maskRow, {
    applyLayout: false,
    setColumnsVisible,
    clearHeaderFiltersWhenEmpty,
    applyColumnVisibilityFn,
    applyFilters: true,
    applySort: true,
    forceFrozenId,
  });
}

/** GET /api/companies/paged — text, multiselect (validity), types CSV. */
export function appendMaintenanceCompanyListPagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  const str = String(v).trim();
  if (str === "") return;
  extra[`filter_${f.field}`] = str;
}

/**
 * mntn023: jeden header filter → `filter_*` (CSV z poľa/objektu, `custom_expiration`).
 */
export function appendMaintenanceStateListPagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  let str = String(v).trim();
  if (str === "") return;
  if (f.field === "custom_expiration") {
    const parts = str.split(",").map((s) => s.trim()).filter(Boolean).filter((p) => p !== "Všetko");
    if (parts.length === 0) return;
    str = parts.join(",");
  }
  extra[`filter_${filterFieldDotsToUnderscore(f.field)}`] = str;
}

/** `filter_${field}` bez zmeny názvu poľa; hodnota len ak `String(value).trim()` nie je prázdna. */
export function appendLiteralFieldPagedFilterNonEmpty(f, extra) {
  if (f?.field == null) return;
  const v = f.value;
  if (v === undefined || v === null || String(v).trim() === "") return;
  extra[`filter_${f.field}`] = v;
}

/** `filter_${field}` s `String.trim()` (iba primitíva, nie objekty). */
export function appendLiteralFieldTrimPagedFilter(f, extra) {
  if (f?.field == null || f.value == null || typeof f.value === "object") return;
  const str = String(f.value).trim();
  if (str === "") return;
  extra[`filter_${f.field}`] = str;
}

/** Výpadky strojov (mntn037): `machine_type.name` → `machine_type_name`; multiselect → CSV. */
export function appendMaintenanceMachineOutageStatePagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  const str = String(v).trim();
  if (str === "") return;
  const field = f.field === "machine_type.name" ? "machine_type_name" : f.field;
  extra[`filter_${field}`] = str;
}

/** mntn038 — intervaly výpadkov: `filter_${field}` literal; multiselect → CSV. */
export function appendMaintenanceMachineOutageIntervalsPagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  const str = String(v).trim();
  if (str === "") return;
  extra[`filter_${f.field}`] = str;
}

/**
 * Výpadky strojov – remote paged: maska pred `tableBuilt`, potom hlavičky; `search`, `filter_currentState` (preview).
 */
export function mergePagedMachineOutageStateListFiltersAndSorters(extra, {
  getTable,
  tableBuiltForParams,
  userMaskFilters,
  maskPage,
  useMaskFromRedux = true,
  searchValue,
  preview,
}) {
  const table = typeof getTable === "function" ? getTable() : getTable;
  const maskRow = useMaskFromRedux && userMaskFilters && maskPage ? findUserMaskRow(userMaskFilters, maskPage) : null;
  let hf = [];
  if (tableBuiltForParams && table) hf = coerceHeaderFilterList(table.getHeaderFilters?.());
  if (tableBuiltForParams && hf.length > 0) {
    hf.forEach((f) => appendMaintenanceMachineOutageStatePagedFilter(f, extra));
  } else if (!tableBuiltForParams && maskRow) {
    parseMaskFilters(maskRow.filters).forEach((f) => appendMaintenanceMachineOutageStatePagedFilter(f, extra));
  }
  mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams, maskRow, when: MASK_SORTERS_WHEN.TABLE_NOT_BUILT });
  if (searchValue && String(searchValue).trim() !== "") extra.search = String(searchValue).trim();
  if (preview && ["in_fault", "working_limited", "functional"].includes(preview)) extra.filter_currentState = preview;
}

/** Zoznam strojov podľa typu údržby (bodky → `_`, objektové hodnoty → CSV). */
export function appendMaintenanceMachineDetailTypePagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  const str = String(v).trim();
  if (str === "") return;
  extra[`filter_${filterFieldDotsToUnderscore(f.field)}`] = str;
}

/** mntn020 — rovnaký formát `filter_*` ako mntn022. */
export const appendMaintenanceRecordDetailPagedFilter = appendMaintenanceMachineDetailTypePagedFilter;

/** mntn021 — rovnaký formát `filter_*` ako mntn001 (maintenance_types/paged). */
export const appendMaintenanceMachineListPagedFilter = appendMaintenanceMachineDetailTypePagedFilter;

/** MaintenanceRecordList: `filter_${field}` bez náhrady bodiek, CSV z poľa. */
export function appendMaintenanceRecordListPagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  const str = String(v).trim();
  if (str === "") return;
  extra[`filter_${f.field}`] = str;
}

const SERVICE_TASK_CLEARABLE_FIELDS = [
  "type",
  "theme",
  "id",
  "defined_id",
  "machine.name",
  "waiting_for_action_by",
  "created_at",
  "updated_at",
  "status_indicator",
  "workflow_next_step_label",
];

/**
 * Service task (Error/Need) – mapovanie header filtrov na query (state list / detail).
 * `definedIdMapsToTheme`: true len pre MaintenanceTaskStateList (`defined_id` → `filter_theme`).
 */
export function applyMaintenanceServiceTaskFiltersArrayToExtra(extra, hf, options = {}) {
  if (!Array.isArray(hf)) return;
  const parseHeaderMulti = (headerValue) => {
    if (headerValue == null || headerValue === "") return [];
    if (Array.isArray(headerValue)) return headerValue.map((x) => String(x ?? "").trim()).filter(Boolean);
    if (typeof headerValue === "object" && Array.isArray(headerValue?.value)) {
      return headerValue.value.map((x) => String(x ?? "").trim()).filter(Boolean);
    }
    return String(headerValue).split(/[\s,;|]+/).map((x) => x.trim()).filter(Boolean);
  };
  const {
    service_states = [],
    userId,
    module_settings,
    getDateFormat,
    definedIdMapsToTheme = false,
  } = options;
  const clearableToKey = definedIdMapsToTheme
    ? {
        type: "filter_type",
        theme: "filter_theme",
        defined_id: "filter_theme",
        id: "filter_id",
        "machine.name": "filter_machine",
        waiting_for_action_by: "filter_waiting_for_action_by",
        created_at: "filter_created_at",
        updated_at: "filter_updated_at",
        status_indicator: "filter_status_indicator",
        workflow_next_step_label: "filter_workflow_next_step_phase",
      }
    : {
        type: "filter_type",
        theme: "filter_theme",
        defined_id: "filter_defined_id",
        id: "filter_id",
        "machine.name": "filter_machine",
        waiting_for_action_by: "filter_waiting_for_action_by",
        created_at: "filter_created_at",
        updated_at: "filter_updated_at",
        status_indicator: "filter_status_indicator",
        workflow_next_step_label: "filter_workflow_next_step_phase",
      };

  hf.forEach((f) => {
    if (f?.field == null) return;
    const v = f.value;
    const strVal = v === undefined || v === null ? "" : typeof v === "string" ? v.trim() : String(v);
    if (strVal === "" && SERVICE_TASK_CLEARABLE_FIELDS.includes(f.field)) {
      const key = clearableToKey[f.field];
      if (key) extra[key] = "";
      return;
    }
    if (strVal === "" && f.field !== "is_unread") return;
    if (f.field === "status_id") {
      const selectedIds = parseHeaderMulti(v).map((x) => Number(x)).filter((n) => !Number.isNaN(n));
      if (selectedIds.length > 0) {
        extra.filter_status_ids = selectedIds.join(",");
      }
    } else if (f.field === "contractor_id") {
      if (v === userId) extra.filter_contractorValue = "Mnou vytvorené";
      else extra.filter_contractor_id = v;
    } else if (f.field === "type") {
      const selected = parseHeaderMulti(v);
      if (selected.length > 1) extra.filter_type = selected.join(",");
      else if (selected.length === 1) extra.filter_type = selected[0];
    } else if (f.field === "theme") {
      extra.filter_theme = strVal;
    } else if (f.field === "defined_id") {
      extra[definedIdMapsToTheme ? "filter_theme" : "filter_defined_id"] = strVal;
    } else if (f.field === "id") {
      extra.filter_id = strVal;
    } else if (f.field === "machine.name") {
      extra.filter_machine = strVal;
    } else if (f.field === "waiting_for_action_by") {
      extra.filter_waiting_for_action_by = strVal;
    } else if (f.field === "created_at") {
      extra.filter_created_at = strVal;
    } else if (f.field === "updated_at") {
      extra.filter_updated_at = strVal;
    } else if (f.field === "workflow_next_step_label") {
      const selected = parseHeaderMulti(v).filter((x) => WORKFLOW_NEXT_STEP_PHASES.includes(x));
      if (selected.length > 0) extra.filter_workflow_next_step_phase = selected.join(",");
    } else if (f.field === "is_unread") {
      const selected = parseHeaderMulti(v).map((x) => String(x).toLowerCase());
      const hasTrue = selected.includes("true") || selected.includes("1");
      const hasFalse = selected.includes("false") || selected.includes("0");
      if (hasTrue && hasFalse) {
        delete extra.filter_is_unread;
      } else if (hasTrue || v === true || v === "true") {
        extra.filter_is_unread = "1";
      } else if (hasFalse || v === false || v === "false") {
        extra.filter_is_unread = "0";
      }
    } else if (f.field === "status_indicator" && strVal) {
      extra.filter_status_indicator = strVal;
    }
  });

  const dateFilterFields = ["created_at", "updated_at", "waiting_for_action_by"];
  const hasDateFilter = hf.some((f) => f?.field && dateFilterFields.includes(f.field) && String(f?.value ?? "").trim() !== "");
  if (hasDateFilter) extra.filter_date_format = getDateFormat?.(module_settings) ?? "dd.MM.yyyy";
}

function headerFiltersHaveNonEmptyValue(hf) {
  if (!Array.isArray(hf) || hf.length === 0) return false;
  return hf.some((f) => {
    const v = f?.value;
    if (v === undefined || v === null) return false;
    if (typeof v === "object") {
      if (Array.isArray(v)) return v.length > 0;
      if (Array.isArray(v?.value)) return v.value.length > 0;
      return false;
    }
    return String(v).trim() !== "";
  });
}

export function mergeMaintenanceServiceTaskPagedListFiltersAndSorters(extra, {
  getTable,
  tableBuiltForParams,
  userMaskFilters,
  maskPage,
  service_states,
  userId,
  module_settings,
  getDateFormat,
  definedIdMapsToTheme = false,
}) {
  const table = typeof getTable === "function" ? getTable() : getTable;
  const maskRow = userMaskFilters && maskPage ? findUserMaskRow(userMaskFilters, maskPage) : null;
  const ctx = { service_states, userId, module_settings, getDateFormat, definedIdMapsToTheme };
  let hf = [];
  if (tableBuiltForParams && table) hf = coerceHeaderFilterList(table.getHeaderFilters?.());
  if (tableBuiltForParams && table) {
    if (headerFiltersHaveNonEmptyValue(hf)) {
      applyMaintenanceServiceTaskFiltersArrayToExtra(extra, hf, ctx);
    } else if (maskRow) {
      applyMaintenanceServiceTaskFiltersArrayToExtra(extra, parseMaskFilters(maskRow.filters), ctx);
    }
  } else if (!tableBuiltForParams && maskRow) {
    applyMaintenanceServiceTaskFiltersArrayToExtra(extra, parseMaskFilters(maskRow.filters), ctx);
  }
  mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams, maskRow, when: MASK_SORTERS_WHEN.TABLE_NOT_BUILT });
}

/**
 * Support tickets list: uložený objekt filtrov z user masky (horné selecty + hodnoty header filtrov) → query `filter_*`.
 */
export function applySupportTicketSavedFiltersObjectToQueryExtra(extra, rawFilters) {
  if (!rawFilters || typeof rawFilters !== "object" || Array.isArray(rawFilters)) return;
  if (rawFilters.contractorValue && rawFilters.contractorValue !== "Všetky") {
    extra.filter_contractorValue = rawFilters.contractorValue;
  }
  if (rawFilters.typeValue && rawFilters.typeValue !== "All") extra.filter_type = rawFilters.typeValue;
  if (rawFilters.theme != null && String(rawFilters.theme).trim()) extra.filter_theme = String(rawFilters.theme).trim();
  if (rawFilters.defined_id != null && String(rawFilters.defined_id).trim()) extra.filter_defined_id = String(rawFilters.defined_id).trim();
  if (rawFilters.priority != null && String(rawFilters.priority).trim()) extra.filter_priority = String(rawFilters.priority).trim();
  if (rawFilters.status != null && String(rawFilters.status).trim()) extra.filter_status = String(rawFilters.status).trim();
  if (rawFilters.mask_number != null && String(rawFilters.mask_number).trim()) extra.filter_mask_number = String(rawFilters.mask_number).trim();
  if (rawFilters.waiting_for_action_by != null && String(rawFilters.waiting_for_action_by).trim()) {
    extra.filter_waiting_for_action_by = String(rawFilters.waiting_for_action_by).trim();
  }
  if (rawFilters.created_at != null && String(rawFilters.created_at).trim()) extra.filter_created_at = String(rawFilters.created_at).trim();
  if (rawFilters.updated_at != null && String(rawFilters.updated_at).trim()) extra.filter_updated_at = String(rawFilters.updated_at).trim();
  if (rawFilters.is_unread === true || rawFilters.is_unread === "true") extra.filter_is_unread = "1";
  else if (rawFilters.is_unread === false || rawFilters.is_unread === "false") extra.filter_is_unread = "0";
  applySupportTicketListScopeStatusParam(extra, rawFilters.statusValue);
}

/**
 * Support tickets list: pole header filtrov z Tabulatora → `filter_*` (parita s `SupportTicketList` buildRequestParams).
 */
export function applySupportTicketHeaderFiltersArrayToExtra(extra, hf, { module_settings, getDateFormat } = {}) {
  const canBeCleared = ["type", "defined_id", "theme", "priority", "status", "mask_number", "waiting_for_action_by", "created_at", "updated_at"];
  const clearableToKey = {
    type: "filter_type",
    defined_id: "filter_defined_id",
    theme: "filter_theme",
    priority: "filter_priority",
    status: "filter_status",
    mask_number: "filter_mask_number",
    waiting_for_action_by: "filter_waiting_for_action_by",
    created_at: "filter_created_at",
    updated_at: "filter_updated_at",
  };
  hf.forEach((f) => {
    if (f?.field == null) return;
    const v = f.value;
    const strVal = v === undefined || v === null ? "" : typeof v === "string" ? v.trim() : String(v);
    if (strVal === "" && canBeCleared.includes(f.field)) {
      const key = clearableToKey[f.field];
      if (key) extra[key] = "";
      return;
    }
    if (strVal === "" && f.field !== "is_unread") return;
    if (f.field === "type") extra.filter_type = strVal;
    else if (f.field === "theme") extra.filter_theme = strVal;
    else if (f.field === "defined_id") extra.filter_defined_id = strVal;
    else if (f.field === "priority") extra.filter_priority = strVal;
    else if (f.field === "status") {
      const selected = parseSupportTicketStatusHeaderValues(v).filter((x) => !isSupportTicketScopeValue(x));
      extra.filter_status = selected.length === 0 ? "" : selected.length === 1 ? selected[0] : selected.join(",");
    }
    else if (f.field === "mask_number") extra.filter_mask_number = strVal;
    else if (f.field === "waiting_for_action_by") extra.filter_waiting_for_action_by = strVal;
    else if (f.field === "created_at") extra.filter_created_at = strVal;
    else if (f.field === "updated_at") extra.filter_updated_at = strVal;
    else if (f.field === "is_unread") {
      if (v === true || v === "true") extra.filter_is_unread = "1";
      else if (v === false || v === "false") extra.filter_is_unread = "0";
    }
  });
  const dateFilterFields = ["created_at", "updated_at", "waiting_for_action_by"];
  const hasDateFilter = hf.some((f) => f?.field && dateFilterFields.includes(f.field) && String(f?.value ?? "").trim() !== "");
  if (hasDateFilter) {
    const mod = module_settings || [];
    extra.filter_date_format = getDateFormat?.(mod) ?? "dd.MM.yyyy";
  }
}

export function mergeSupportTicketPagedListFiltersAndSorters(extra, {
  getTable,
  tableBuiltForParams,
  userMaskFilters,
  maskPage,
  module_settings,
  getDateFormat,
}) {
  const table = typeof getTable === "function" ? getTable() : getTable;
  const maskRow = userMaskFilters && maskPage ? findUserMaskRow(userMaskFilters, maskPage) : null;
  const ctx = { module_settings, getDateFormat };
  let hf = [];
  if (tableBuiltForParams && table) hf = coerceHeaderFilterList(table.getHeaderFilters?.());
  if (tableBuiltForParams && hf.length > 0) {
    applySupportTicketHeaderFiltersArrayToExtra(extra, hf, ctx);
  } else if (!tableBuiltForParams && maskRow) {
    const saved = safeParse(maskRow.filters, {});
    if (Array.isArray(saved) && saved.length > 0) {
      applySupportTicketHeaderFiltersArrayToExtra(extra, saved, ctx);
    } else if (saved && typeof saved === "object" && !Array.isArray(saved)) {
      applySupportTicketSavedFiltersObjectToQueryExtra(extra, saved);
    }
  }
  mergePagedSortersIntoExtra(extra, { table, tableBuiltForParams, maskRow, when: MASK_SORTERS_WHEN.TABLE_NOT_BUILT });
}

/** Certifikáty podľa stavu (mntn010 tree) – multiselect CSV, `custom_expiration` bez „Všetko“. */
export function appendMaintenanceCompanyCertStateListPagedFilter(f, extra) {
  if (f?.field == null) return;
  let v = f.value;
  if (v === undefined || v === null) return;
  if (typeof v === "object") {
    if (Array.isArray(v)) {
      v = v.map((x) => String(x ?? "")).join(",");
    } else if (Array.isArray(v?.value)) {
      v = v.value.map((x) => String(x ?? "")).join(",");
    } else return;
  }
  let val = String(v).trim();
  if (val === "") return;
  if (f.field === "custom_expiration") {
    const parts = val.split(",").map((s) => s.trim()).filter(Boolean).filter((p) => p !== "Všetko");
    if (parts.length === 0) return;
    val = parts.join(",");
  }
  extra[`filter_${f.field}`] = val;
}

/** Kanonické poradie stĺpcov pre mntn010 (tree). */
export const MAINTENANCE_COMPANY_CERT_STATE_LIST_FIELD_ORDER = Object.freeze([
  "id",
  "company.name",
  "description",
  "name",
  "custom_start_of_validity",
  "custom_end_of_validity",
  "custom_expiration",
  "record_description",
  "responsible.name",
  "certificate_status",
  "state",
  "protocol",
  "created_at",
  "updated_at",
]);

export const mergeMaintenanceCompanyCertStateListColumnLayoutAttributes = mergeChecklistDetailColumnLayoutAttributes;
export const applyMaintenanceCompanyCertStateListColumnLayout = applyChecklistDetailColumnLayout;

export function restoreMaintenanceCompanyCertStateListUserMaskToTable(table, maskRow, options = {}) {
  if (!table || !maskRow) return;
  const {
    setColumnsVisible,
    applyColumnVisibilityFn,
    clearHeaderFiltersWhenEmpty = true,
  } = options;

  applyMaintenanceCompanyCertStateListColumnLayout(
    table,
    safeParse(maskRow.attributes, []),
    MAINTENANCE_COMPANY_CERT_STATE_LIST_FIELD_ORDER
  );
  applyUserMaskRowToTabulator(table, maskRow, {
    applyLayout: false,
    setColumnsVisible,
    clearHeaderFiltersWhenEmpty,
    applyColumnVisibilityFn,
    applyFilters: true,
    applySort: true,
  });
}

/** Kontakty firmy (add) – `lecturer.name` → `lecturer_name`; ostatné ako company list paged. */
export function appendMaintenanceCompanyContactAddPagedFilter(f, extra) {
  if (f?.field == null) return;
  if (f.field === "lecturer.name") {
    appendMaintenanceCompanyListPagedFilter({ ...f, field: "lecturer_name" }, extra);
    return;
  }
  appendMaintenanceCompanyListPagedFilter(f, extra);
}

/** Certifikáty firmy (embedded add/detail) – tree/flat; `cert_description` → `record_description`. */
export function appendMaintenanceCompanyCertificateEmbeddedPagedFilter(f, extra) {
  if (f?.field == null) return;
  if (f.field === "cert_description") {
    appendMaintenanceCompanyListPagedFilter({ ...f, field: "record_description" }, extra);
    return;
  }
  appendMaintenanceCompanyListPagedFilter(f, extra);
}

export const mergeMaintenanceCompanyEmbeddedColumnLayoutAttributes = mergeChecklistDetailColumnLayoutAttributes;
export const applyMaintenanceCompanyEmbeddedColumnLayout = applyChecklistDetailColumnLayout;

export function restoreMaintenanceCompanyEmbeddedUserMaskToTable(table, maskRow, options = {}) {
  if (!table || !maskRow) return;
  const {
    canonicalFieldOrder,
    setColumnsVisible,
    applyColumnVisibilityFn,
    clearHeaderFiltersWhenEmpty = true,
  } = options;
  if (!canonicalFieldOrder) return;

  applyMaintenanceCompanyEmbeddedColumnLayout(table, safeParse(maskRow.attributes, []), canonicalFieldOrder);
  applyUserMaskRowToTabulator(table, maskRow, {
    applyLayout: false,
    setColumnsVisible,
    clearHeaderFiltersWhenEmpty,
    applyColumnVisibilityFn,
    applyFilters: true,
    applySort: true,
  });
}

/** Typy aktualizácie – musia sedieť s UserMaskFilterController (PHP). */
export const USER_MASK_UPDATE_TYPE = Object.freeze({
  SET_FILTERS: "set_filters",
  SET_SORTERS: "set_sorters",
  SET_COLUMNS: "set_columns",
  COLUMN_LAYOUT: "column_move",
  SET_ATTRIBUTES: "set_attributes",
  ALL: "all",
});

/**
 * Header filtre z Tabulatora (alebo už pole) → bezpečné { field, value } pre JSON/DB.
 */
export function normalizeHeaderFiltersForMask(filters) {
  if (!Array.isArray(filters)) return [];
  return filters
    .map((f) => {
      const field =
        typeof f?.field === "string"
          ? f.field
          : typeof f?.column === "string"
            ? f.column
            : f?.column?.getField?.() ?? null;
      if (field == null || field === "") return null;
      let value = f?.value;
      if (value !== null && value !== undefined && typeof value === "object") {
        try {
          value = JSON.parse(JSON.stringify(value));
        } catch {
          value = String(value);
        }
      }
      return { field, value };
    })
    .filter(Boolean);
}

export function serializeHeaderFiltersForMask(table) {
  const raw = table?.getHeaderFilters?.() ?? [];
  return normalizeHeaderFiltersForMask(raw);
}

/** Triedenie z tabuľky → tvar vhodný na uloženie (rovnaký ako API očakáva po mapovaní v UtilContext). */
export function serializeSortersForMask(table) {
  if (!table || table.initialized === false) return [];
  return cleanSorters(table?.getSorters?.() ?? []);
}

/**
 * Safe getSorters for progressive lists — only after tableBuilt / initialized.
 * Avoids Tabulator "Table Not Initialized - Calling the getSorters function…" warnings.
 */
export function safeGetSorters(table, { tableBuilt = true } = {}) {
  if (!tableBuilt || !table || table.initialized === false) return [];
  if (typeof table.getSorters !== 'function') return [];
  return table.getSorters() ?? [];
}

/**
 * Aplikuje jeden záznam user_mask_filters na Tabulator (layout, viditeľnosť, triedenie, header filtre).
 * Zodpovedá parsovaniu pri ukladaní cez setUserMaskFilter (parseMaskFilters, parseSorters, safeParse).
 *
 * @param {object|null|undefined} table
 * @param {object|null|undefined} maskRow
 * @param {object} [options]
 * @param {(m: object) => void} [options.setColumnsVisible]
 * @param {boolean} [options.clearHeaderFiltersWhenEmpty=false]
 * @param {boolean} [options.applyLayout=true]
 * @param {boolean} [options.applyColumnVisibility=true]
 * @param {(table: object, columnsParsed: object, setColumnsVisible: function) => void} [options.applyColumnVisibilityFn] – namiesto predvoleného hide/show podľa kľúčov v JSON
 * @param {boolean} [options.applySort=true]
 * @param {boolean} [options.applyFilters=true]
 * @param {boolean} [options.forceFrozenId=false] — flat remote Skupina A (V9d-1)
 */
export function applyUserMaskRowToTabulator(table, maskRow, options = {}) {
  if (!table || !maskRow) return;

  const {
    setColumnsVisible,
    clearHeaderFiltersWhenEmpty = false,
    applyLayout = true,
    applyColumnVisibility = true,
    applyColumnVisibilityFn = null,
    applySort = true,
    applyFilters = true,
    forceFrozenId = false,
  } = options;

  const layoutSanitizeOptions = forceFrozenId ? { forceFrozenId: true } : undefined;
  const attributes = sanitizeTabulatorLayoutAttributes(safeParse(maskRow.attributes, []), layoutSanitizeOptions);
  const filters = applyFilters ? parseMaskFilters(maskRow.filters) : [];
  const sorters = applySort ? parseSorters(maskRow.sorters) : [];
  const json_columns = safeParse(maskRow.columns, {});

  if (applyLayout && Array.isArray(attributes) && attributes.length > 1) {
    table.setColumnLayout?.(attributes);
  }

  if (
    applyColumnVisibility &&
    json_columns &&
    typeof json_columns === "object" &&
    setColumnsVisible
  ) {
    if (typeof applyColumnVisibilityFn === "function") {
      applyColumnVisibilityFn(table, json_columns, setColumnsVisible);
    } else {
      setColumnsVisible(json_columns);
      for (const column in json_columns) {
        if (json_columns[column] === false) table.hideColumn?.(column);
        else table.showColumn?.(column);
      }
    }
  }

  // Header filtre pred triedením: pri remote sortMode inak setSort spustí ajax len s triedením (bez filtrov z masky).
  if (applyFilters) {
    if (filters.length > 0) {
      filters.forEach((f) =>
        table.setHeaderFilterValue?.(f.field, f.value ?? "", true)
      );
    } else if (clearHeaderFiltersWhenEmpty) {
      table.clearHeaderFilter?.();
    }
  }

  if (applySort && sorters.length > 0) {
    table.setSort?.(sorters.map((s) => ({ column: s.column ?? s.field, dir: s.dir })));
  }
}

/** Len page a size z Tabulator params – filter objekt by sa zlýhal na [object Object] */
export function extractSafePaginationParams(params) {
  const extra = {};
  const safeNum = (v) => (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : undefined);
  if (safeNum(params?.page) != null) extra.page = params.page;
  if (safeNum(params?.size) != null) extra.size = params.size;
  return extra;
}

const CACHE_TTL_MS = 2000;

const EMPTY_PAGED_RESPONSE = { data: [], last_page: 1, total: 0, current_page: 1 };

/**
 * Refresh tabuľky s progressive load od začiatku: zobrazí loading, vyčistí cache,
 * vyčistí dáta a spustí replaceData() aby sa nanovo načítala prvá dávka a pri scrollovaní ďalšie.
 * @param {() => T} getTable - funkcia vracajúca inštanciu Tabulator
 * @param {{ current: Map } | undefined} completedCacheRef - ref na cache (createDedupedAjaxRequest); ak chýba, cache sa nečistí
 * @param {string} [loadingMessage] - text pre t.alert(); default Common.LoadingData
 */
export function refreshProgressiveFromStart(getTable, completedCacheRef, loadingMessage) {
  const table = getTable?.();
  const msg = loadingMessage == null || loadingMessage === '' ? uiT('Common.LoadingData') : loadingMessage;
  if (table?.alert) table.alert(msg);
  if (completedCacheRef?.current) completedCacheRef.current.clear();
  if (table?.setData) table.setData([]);
  table?.replaceData?.();
}

/**
 * Soft progressive reset without setData([]).
 * Avoids Tabulator "Data Load Response Blocked" when another ajax is active:
 * keeps existing rows until replaceData page-1 arrives.
 */
export function softRefreshProgressiveFromStart(getTable, completedCacheRef, loadingMessage) {
  const table = getTable?.();
  const msg = loadingMessage == null || loadingMessage === '' ? uiT('Common.LoadingData') : loadingMessage;
  if (table?.alert) table.alert(msg);
  if (completedCacheRef?.current) completedCacheRef.current.clear();
  return Promise.resolve(table?.replaceData?.()).finally(() => {
    table?.clearAlert?.();
  });
}

/**
 * Serialized + coalesced progressive refresh controller.
 * Rapid filter/sort changes share one latest-wins refresh; never calls setData([]).
 *
 * @param {object} opts
 * @param {() => any} opts.getTable
 * @param {{ current: Map }} [opts.completedCacheRef]
 * @param {{ current: { sig: any, promise: any } }} [opts.inFlightRef]
 * @param {{ current: number }} [opts.generationRef] - bumped per scheduled refresh
 * @param {number} [opts.coalesceMs=50]
 * @param {string} [opts.loadingMessage]
 * @param {() => void} [opts.onBeforeRefresh]
 * @returns {{ schedule: (opts?: { immediate?: boolean }) => void, cancel: () => void, flush: () => Promise<void> }}
 */
export function createSerializedProgressiveRefreshController({
  getTable,
  completedCacheRef,
  inFlightRef,
  generationRef,
  coalesceMs = 50,
  loadingMessage,
  onBeforeRefresh,
} = {}) {
  let timer = null;
  let chain = Promise.resolve();
  let latestToken = 0;

  const bumpGeneration = () => {
    if (!generationRef) return 0;
    generationRef.current = (generationRef.current || 0) + 1;
    return generationRef.current;
  };

  const runRefresh = (token) => {
    chain = chain.catch(() => {}).then(async () => {
      if (token !== latestToken) return;
      const pending = inFlightRef?.current?.promise;
      if (pending) {
        try {
          await pending;
        } catch (_) {
          /* ignore aborted/failed prior */
        }
      }
      if (token !== latestToken) return;
      if (typeof onBeforeRefresh === 'function') {
        try {
          onBeforeRefresh();
        } catch (_) {
          /* ignore */
        }
      }
      if (completedCacheRef?.current) completedCacheRef.current.clear();
      if (inFlightRef) inFlightRef.current = { sig: null, promise: null };
      const table = typeof getTable === 'function' ? getTable() : getTable;
      if (!table?.replaceData) return;
      const msg = loadingMessage == null || loadingMessage === '' ? uiT('Common.LoadingData') : loadingMessage;
      try {
        table.alert?.(msg);
        await Promise.resolve(table.replaceData());
      } finally {
        if (token === latestToken) {
          table.clearAlert?.();
        }
      }
    });
    return chain;
  };

  const schedule = ({ immediate = false } = {}) => {
    bumpGeneration();
    const token = ++latestToken;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const delay = immediate ? 0 : Math.max(0, Number(coalesceMs) || 0);
    if (delay === 0) {
      runRefresh(token);
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      runRefresh(token);
    }, delay);
  };

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    latestToken += 1;
    bumpGeneration();
  };

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      const token = latestToken;
      return runRefresh(token);
    }
    return chain.catch(() => {});
  };

  return { schedule, cancel, flush };
}

/**
 * Zachytí horizontálny scroll (ak je ref) a obnoví progressive tabuľku od začiatku.
 * Rovnaký vzor ako po sebe volané captureHScroll + refreshFromStart na stránkach.
 */
export function captureHScrollAndRefreshFromStart(getTable, completedCacheRef, savedHScrollRef, loadingMessage) {
  if (savedHScrollRef) {
    const t = getTable?.();
    const holder = t?.rowManager?.element;
    if (holder) {
      savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
    }
  }
  refreshProgressiveFromStart(getTable, completedCacheRef, loadingMessage);
}

/**
 * Vytvorí deduplikovaný ajaxRequestFunc.
 * @param {Function} buildParams - (paramsFromTabulator) => mergedParams
 * @param {Function} fetchFn - (url, params) => Promise
 * @param {{ current: { sig, promise } }} inFlightRef
 * @param {{ current: Map }} completedCacheRef
 * @param {{ current: boolean }} [skipRef] - keď true, neposielať request a vrátiť lastResponseRef
 * @param {{ current: object }} [lastResponseRef] - posledná úspešná odpoveď (pri skip)
 */
export function createDedupedAjaxRequest(buildParams, fetchFn, inFlightRef, completedCacheRef, skipRef, lastResponseRef) {
  return (url, config, params) => {
    // Počas obnovy masky preskočíme len ak už máme odpoveď – inak by prvý request vrátil prázdne
    if (skipRef?.current && lastResponseRef?.current != null) {
      return Promise.resolve(lastResponseRef.current);
    }

    const merged = buildParams(params || {});
    const sig = url + (merged && Object.keys(merged).length ? "?" + stableStringify(merged) : "");

    if (inFlightRef.current.sig === sig && inFlightRef.current.promise) {
      return inFlightRef.current.promise;
    }
    const cached = completedCacheRef.current.get(sig);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return Promise.resolve(cached.data);

    const now = Date.now();
    completedCacheRef.current.forEach((v, k) => {
      if (now - v.ts > CACHE_TTL_MS) completedCacheRef.current.delete(k);
    });

    // Claim the in-flight slot synchronously BEFORE fetchFn so identical concurrent
    // Tabulator owners (initial ajax + Filter.initialHeaderFilter) share one GET.
    // Promise shell is assigned before fetchFn so sync re-entry during fetchFn also joins.
    let resolveOuter;
    let rejectOuter;
    const promise = new Promise((resolve, reject) => {
      resolveOuter = resolve;
      rejectOuter = reject;
    });
    inFlightRef.current = { sig, promise };
    fetchFn(url, merged).then(
      (data) => {
        if (inFlightRef.current.sig === sig) {
          completedCacheRef.current.set(sig, { ts: Date.now(), data });
          if (lastResponseRef) lastResponseRef.current = data;
          inFlightRef.current = { sig: null, promise: null };
        }
        resolveOuter(data);
      },
      (err) => {
        if (inFlightRef.current.sig === sig) inFlightRef.current = { sig: null, promise: null };
        rejectOuter(err);
      }
    );
    return promise;
  };
}

/**
 * Kontrola, či riadok (data) prechádza aktuálnymi header filtrami.
 * Používa sa v scheduleRedraw: ak po update (Pusher/detail) riadok už nespadá pod filtre, odstráni sa.
 * @param {object} data - dáta riadka (getData())
 * @param {Array<{field: string, value: any}>} headerFilters - getHeaderFilters() z tabuľky
 * @returns {boolean}
 */
function getChecklistRowFieldValue(data, field) {
  if (field === 'template.machine.name') return data?.template?.machine?.name;
  if (field === 'template.name') return data?.template?.name;
  return data?.[field];
}

/** Lokálna kontrola riadku po Pusher update (mntn029) — zjednodušená zhoda s header filtrami. */
export function rowPassesChecklistHeaderFilters(data, headerFilters) {
  if (!Array.isArray(headerFilters)) return true;
  for (const f of headerFilters) {
    if (!f?.field) continue;
    const rawVal = f.value;
    const filterEmpty = rawVal === undefined || rawVal === null || String(rawVal).trim() === "";
    if (filterEmpty) continue;
    const filterStr = String(rawVal).trim();
    const rowVal = getChecklistRowFieldValue(data, f.field);
    const rowStr = rowVal === undefined || rowVal === null ? "" : String(rowVal).trim();
    if (f.field === 'checklist_status') {
      const parts = filterStr.split(',').map((x) => x.trim()).filter(Boolean);
      if (parts.length === 0) continue;
      if (parts.length >= 3) continue;
      if (!rowStr || !parts.includes(rowStr)) return false;
      continue;
    }
    if (f.field === 'process_status') {
      const parts = filterStr.split(',').map((x) => x.trim()).filter(Boolean);
      if (parts.length === 0) continue;
      if (!parts.includes(rowStr)) return false;
      continue;
    }
    if (f.field === 'is_unread') {
      const filterBool = filterStr === 'true' || filterStr === '1';
      const rowBool = rowVal === true || rowVal === 1 || rowVal === '1' || String(rowVal).toLowerCase() === 'true';
      if (filterBool !== rowBool) return false;
      continue;
    }
    if (f.field === 'created_at' || f.field === 'updated_at') {
      continue;
    }
    if (f.field === 'id') {
      if (!rowStr.includes(filterStr)) return false;
      continue;
    }
    const filterNorm = filterStr.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const rowNorm = rowStr.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (!rowNorm.includes(filterNorm)) return false;
  }
  return true;
}

/**
 * Kontrola, či service task riadok (maintenance – Error/Need) prechádza aktuálnymi header filtrami.
 * @param {object} data - dáta riadka (getData())
 * @param {Array<{field: string, value: any}>} headerFilters - getHeaderFilters() z tabuľky
 * @param {{ service_states?: Array<{id, name}>, userId?: string|number }} options
 * @returns {boolean}
 */
export function rowPassesMaintenanceTaskHeaderFilters(data, headerFilters, options = {}) {
  if (!Array.isArray(headerFilters)) return true;
  const { service_states = [], userId } = options;
  for (const f of headerFilters) {
    if (!f?.field) continue;
    const rawVal = f.value;
    const filterEmpty = rawVal === undefined || rawVal === null || String(rawVal).trim() === "";
    if (filterEmpty) continue;
    const filterStr = String(rawVal).trim();
    let rowVal;
    if (f.field === "machine.name") {
      rowVal = data?.machine?.name ?? data?.["machine.name"];
    } else {
      rowVal = data?.[f.field];
    }
    const rowStr = rowVal === undefined || rowVal === null ? "" : String(rowVal).trim();
    if (f.field === "status_id") {
      const stateName = service_states.find((s) => Number(s.id) === Number(rawVal))?.name;
      if (stateName) {
        const rowStateName = service_states.find((s) => Number(s.id) === Number(data?.status_id))?.name;
        if (rowStateName !== stateName) return false;
      }
      continue;
    }
    if (f.field === "contractor_id") {
      if (rawVal === userId) {
        if (data?.contractor_id == null || String(data.contractor_id) !== String(userId)) return false;
      } else if (rowStr !== filterStr) return false;
      continue;
    }
    if (f.field === "is_unread") {
      const filterBool = filterStr === "true" || filterStr === "1";
      const rowBool = rowVal === true || rowVal === 1 || rowVal === "1" || String(rowVal).toLowerCase() === "true";
      if (filterBool !== rowBool) return false;
      continue;
    }
    if (rowStr !== filterStr) return false;
  }
  return true;
}

/**
 * Kontrola, či riadok error_record prechádza aktuálnymi header filtrami (pre OTK listy).
 * Backend používa LIKE pre filter_*, takže row hodnota musí obsahovať filter (case insensitive).
 * @param {object} data - dáta riadka (getData())
 * @param {Array<{field: string, value: any}>} headerFilters - getHeaderFilters() z tabuľky
 * @returns {boolean}
 */
export function rowPassesErrorRecordHeaderFilters(data, headerFilters) {
  if (!Array.isArray(headerFilters)) return true;
  for (const f of headerFilters) {
    if (!f?.field) continue;
    const rawVal = f.value;
    const filterEmpty = rawVal === undefined || rawVal === null || String(rawVal).trim() === "";
    if (filterEmpty) continue;
    const filterStr = String(rawVal).trim().toLowerCase();
    let rowVal;
    if (f.field === "customer.name" || f.field === "customer_name") rowVal = data?.customer?.name;
    else if (f.field === "record_processor.name" || f.field === "record_processor_name") rowVal = data?.record_processor?.name;
    else if (f.field === "auditor.name" || f.field === "auditor_name") rowVal = data?.auditor?.name;
    else if (f.field === "group.name") rowVal = typeof data?.group?.name === "object" ? (data.group.name?.sk ?? data.group.name) : data?.group?.name;
    else if (f.field === "error_type.name") rowVal = typeof data?.error_type?.name === "object" ? (data.error_type.name?.sk ?? data.error_type.name) : data?.error_type?.name;
    else if (f.field === "prevention.name") rowVal = typeof data?.prevention?.name === "object" ? (data.prevention.name?.sk ?? data.prevention.name) : data?.prevention?.name;
    else if (f.field === "root_cause.name") rowVal = typeof data?.root_cause?.name === "object" ? (data.root_cause.name?.sk ?? data.root_cause.name) : data?.root_cause?.name;
    else rowVal = data?.[f.field];
    const rowStr = (rowVal === undefined || rowVal === null ? "" : String(rowVal)).trim().toLowerCase();
    if (!rowStr.includes(filterStr)) return false;
  }
  return true;
}

/**
 * Kontrola, či state_list riadok (kalibrácia – gauge + custom_*) prechádza aktuálnymi header filtrami.
 * Backend používa LIKE pre filter_*, takže row hodnota musí obsahovať filter (case insensitive).
 * @param {object} data - dáta riadka (getData()) – jeden state_list row
 * @param {Array<{field: string, value: any}>} headerFilters - getHeaderFilters() z tabuľky
 * @returns {boolean}
 */
export function rowPassesCalibrationStateListHeaderFilters(data, headerFilters) {
  if (!Array.isArray(headerFilters)) return true;
  for (const f of headerFilters) {
    if (!f?.field) continue;
    const rawVal = f.value;
    const filterEmpty = rawVal === undefined || rawVal === null || String(rawVal).trim() === "";
    if (filterEmpty) continue;
    const filterStr = String(rawVal).trim().toLowerCase();
    let rowVal;
    if (f.field.includes(".")) {
      const parts = f.field.split(".");
      rowVal = parts.reduce((obj, key) => obj?.[key], data);
    } else {
      rowVal = data?.[f.field];
    }
    const rowStr = (rowVal === undefined || rowVal === null ? "" : String(rowVal)).trim().toLowerCase();
    if (!rowStr.includes(filterStr)) return false;
  }
  return true;
}
